import React, { useState, useEffect, useCallback } from 'react';
import {
  FaQuestionCircle, FaExclamationTriangle, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt,
  FaClock, FaWhatsapp, FaPlus, FaEdit, FaTrash, FaChevronDown, FaHeadset,
} from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import {
  getSupportContent, createSupportItem, updateSupportItem, deleteSupportItem,
  createSupportContact, updateSupportContact, deleteSupportContact,
} from '../services/api';

const EMPTY_ITEM_FORM = { type: 'faq', title: '', description: '' };
const EMPTY_CONTACT_FORM = { label: '', value: '' };

const contactIcon = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('whatsapp')) return <FaWhatsapp />;
  if (l.includes('phone') || l.includes('call')) return <FaPhoneAlt />;
  if (l.includes('email') || l.includes('mail')) return <FaEnvelope />;
  if (l.includes('address') || l.includes('location') || l.includes('office')) return <FaMapMarkerAlt />;
  if (l.includes('hour') || l.includes('time')) return <FaClock />;
  return <FaHeadset />;
};

const contactHref = (label = '', value = '') => {
  const l = label.toLowerCase();
  if (l.includes('email') || l.includes('mail')) return `mailto:${value}`;
  if (l.includes('phone') || l.includes('call') || l.includes('whatsapp')) return `tel:${value.replace(/[^+\d]/g, '')}`;
  return null;
};

const HelpSupport = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const isAdmin = role === 'Admin';

  const [faqs, setFaqs] = useState([]);
  const [problems, setProblems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  const [itemModal, setItemModal] = useState(null); // { type, editId } | null
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);
  const [contactModal, setContactModal] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSupportContent(isAdmin ? { all: 'true' } : undefined);
      const data = res.data?.data || {};
      setFaqs(data.faqs || []);
      setProblems(data.problems || []);
      setContacts(data.contacts || []);
    } catch {
      setFaqs([]); setProblems([]); setContacts([]);
    }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── FAQ / Problem items ──────────────────────────────────────────────────
  const openAddItem = (type) => { setItemForm({ ...EMPTY_ITEM_FORM, type }); setItemModal({ type, editId: null }); };
  const openEditItem = (item) => { setItemForm({ type: item.type, title: item.title, description: item.description }); setItemModal({ type: item.type, editId: item.id }); };
  const closeItemModal = () => { setItemModal(null); setItemForm(EMPTY_ITEM_FORM); };

  const submitItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (itemModal.editId) {
        await updateSupportItem(itemModal.editId, itemForm);
        showToast('Updated!', 'success');
      } else {
        await createSupportItem(itemForm);
        showToast('Added!', 'success');
      }
      closeItemModal();
      fetchAll();
    } catch (err) { showToast(err.message || 'Failed', 'error'); }
    setSaving(false);
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await deleteSupportItem(id); fetchAll(); showToast('Deleted'); }
    catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  // ── Contacts ─────────────────────────────────────────────────────────────
  const openAddContact = () => { setContactForm(EMPTY_CONTACT_FORM); setContactModal({ editId: null }); };
  const openEditContact = (c) => { setContactForm({ label: c.label, value: c.value }); setContactModal({ editId: c.id }); };
  const closeContactModal = () => { setContactModal(false); setContactForm(EMPTY_CONTACT_FORM); };

  const submitContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (contactModal.editId) {
        await updateSupportContact(contactModal.editId, contactForm);
        showToast('Updated!', 'success');
      } else {
        await createSupportContact(contactForm);
        showToast('Added!', 'success');
      }
      closeContactModal();
      fetchAll();
    } catch (err) { showToast(err.message || 'Failed', 'error'); }
    setSaving(false);
  };

  const removeContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try { await deleteSupportContact(id); fetchAll(); showToast('Deleted'); }
    catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  return (
    <div className="page-wrapper">
      <Header title="Help & Support" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaQuestionCircle style={{ color: 'var(--primary)', marginRight: 8 }} />Help & Support</h1>
            <p>FAQs, common problems and ways to reach us</p>
          </div>
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : (
          <>
            {/* ── FAQs ── */}
            <div className="card" style={{ padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}><FaQuestionCircle style={{ marginRight: 8, color: 'var(--primary)' }} />Frequently Asked Questions</h3>
                {isAdmin && <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => openAddItem('faq')}><FaPlus /> Add FAQ</button>}
              </div>
              {faqs.length === 0 ? (
                <p style={{ color: 'var(--text-light)', fontSize: 13 }}>No FAQs added yet.</p>
              ) : faqs.map(f => (
                <div key={f.id} style={{ borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}>
                    <strong style={{ fontSize: 14, color: 'var(--text-dark)', opacity: f.isActive === false ? 0.5 : 1 }}>
                      {f.title}{f.isActive === false && ' (hidden)'}
                    </strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isAdmin && (
                        <>
                          <button className="action-btn action-btn-edit" onClick={e => { e.stopPropagation(); openEditItem(f); }}><FaEdit /></button>
                          <button className="action-btn action-btn-delete" onClick={e => { e.stopPropagation(); removeItem(f.id); }}><FaTrash /></button>
                        </>
                      )}
                      <FaChevronDown style={{ transform: openFaq === f.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--text-light)' }} />
                    </div>
                  </div>
                  {openFaq === f.id && <p style={{ marginTop: 8, fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.6 }}>{f.description}</p>}
                </div>
              ))}
            </div>

            {/* ── Common Problems ── */}
            <div className="card" style={{ padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}><FaExclamationTriangle style={{ marginRight: 8, color: '#e65100' }} />Common Problems & Solutions</h3>
                {isAdmin && <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => openAddItem('problem')}><FaPlus /> Add Problem</button>}
              </div>
              {problems.length === 0 ? (
                <p style={{ color: 'var(--text-light)', fontSize: 13 }}>No common problems listed yet.</p>
              ) : problems.map(p => (
                <div key={p.id} style={{ background: '#fff3e0', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <strong style={{ fontSize: 14, color: '#e65100', opacity: p.isActive === false ? 0.5 : 1 }}>
                      {p.title}{p.isActive === false && ' (hidden)'}
                    </strong>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button className="action-btn action-btn-edit" onClick={() => openEditItem(p)}><FaEdit /></button>
                        <button className="action-btn action-btn-delete" onClick={() => removeItem(p.id)}><FaTrash /></button>
                      </div>
                    )}
                  </div>
                  <p style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.6 }}>{p.description}</p>
                </div>
              ))}
            </div>

            {/* ── Contact Info ── */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}><FaHeadset style={{ marginRight: 8, color: 'var(--primary)' }} />Contact Us</h3>
                {isAdmin && <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={openAddContact}><FaPlus /> Add Contact</button>}
              </div>
              {contacts.length === 0 ? (
                <p style={{ color: 'var(--text-light)', fontSize: 13 }}>No contact information added yet.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                  {contacts.map(c => {
                    const href = contactHref(c.label, c.value);
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, background: '#f8f9fa', borderRadius: 10, opacity: c.isActive === false ? 0.5 : 1 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--primary-bg)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {contactIcon(c.label)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-light)' }}>{c.label}{c.isActive === false && ' (hidden)'}</div>
                          {href ? (
                            <a href={href} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', wordBreak: 'break-word' }}>{c.value}</a>
                          ) : (
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', wordBreak: 'break-word' }}>{c.value}</div>
                          )}
                        </div>
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button className="action-btn action-btn-edit" onClick={() => openEditContact(c)}><FaEdit /></button>
                            <button className="action-btn action-btn-delete" onClick={() => removeContact(c.id)}><FaTrash /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <BottomNav />

      {/* ── Add/Edit FAQ or Problem ── */}
      {itemModal && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && closeItemModal()}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>{itemModal.editId ? 'Edit' : 'Add'} {itemModal.type === 'faq' ? 'FAQ' : 'Problem'}</h2>
              <button className="modal-close" onClick={closeItemModal}>×</button>
            </div>
            <form onSubmit={submitItem}>
              <div className="form-group">
                <label>{itemModal.type === 'faq' ? 'Question *' : 'Problem Title *'}</label>
                <input required value={itemForm.title} onChange={e => setItemForm(p => ({ ...p, title: e.target.value }))}
                  placeholder={itemModal.type === 'faq' ? 'e.g. How do I reset my password?' : 'e.g. Unable to join video call'} />
              </div>
              <div className="form-group">
                <label>{itemModal.type === 'faq' ? 'Answer *' : 'Solution / Steps *'}</label>
                <textarea required rows={5} value={itemForm.description} onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))}
                  placeholder={itemModal.type === 'faq' ? 'Write the answer...' : 'Explain how to resolve it...'} />
              </div>
              {itemModal.editId && (
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={itemForm.isActive !== false} onChange={e => setItemForm(p => ({ ...p, isActive: e.target.checked }))} style={{ marginRight: 8 }} />
                    Visible to doctors/patients
                  </label>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={closeItemModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : itemModal.editId ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add/Edit Contact ── */}
      {contactModal && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && closeContactModal()}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>{contactModal.editId ? 'Edit' : 'Add'} Contact</h2>
              <button className="modal-close" onClick={closeContactModal}>×</button>
            </div>
            <form onSubmit={submitContact}>
              <div className="form-group">
                <label>Label *</label>
                <input required value={contactForm.label} onChange={e => setContactForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Phone, Email, WhatsApp, Office Address, Support Hours" />
              </div>
              <div className="form-group">
                <label>Value *</label>
                <input required value={contactForm.value} onChange={e => setContactForm(p => ({ ...p, value: e.target.value }))} placeholder="e.g. +91 98765 43210" />
              </div>
              {contactModal.editId && (
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={contactForm.isActive !== false} onChange={e => setContactForm(p => ({ ...p, isActive: e.target.checked }))} style={{ marginRight: 8 }} />
                    Visible to doctors/patients
                  </label>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={closeContactModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : contactModal.editId ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSupport;
